import React, { useState, useEffect } from "react";
import { useAuth } from "../AuthContext";
import axios from "axios";
import "./EmployeeDashboard.css";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import GiftModal from "./GiftModal"; // Import GiftModal component
import jsPDF from "jspdf";
import "jspdf-autotable";

const EmployeeDashboard = () => {
  const { mobile } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [schemes, setSchemes] = useState([]);
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [allGifts, setAllGifts] = useState([]);
  const [schemeSearchText, setSchemeSearchText] = useState("");
  const [newSchemes, setNewSchemes] = useState([]); // New state for New Items Schemes
  const [schemeType, setSchemeType] = useState("old"); // To toggle between old and new schemes
  const [filteredSchemes, setFilteredSchemes] = useState([]);

  useEffect(() => {
    const currentSchemes = schemeType === "old" ? schemes : newSchemes;

    const filtered = currentSchemes.filter((scheme) => {
      if (!scheme.productInfo) return false;
      const searchLower = schemeSearchText.toLowerCase();
      const brandMatch = scheme.productInfo.brand_name
        .toLowerCase()
        .includes(searchLower);
      const compositionMatch = scheme.productInfo.compostion
        .toLowerCase()
        .includes(searchLower);
      const segmentMatch = scheme.productInfo.category
        .toLowerCase()
        .includes(searchLower);
      return brandMatch || compositionMatch || segmentMatch;
    });

    setFilteredSchemes(filtered);
  }, [schemeType, schemeSearchText, schemes, newSchemes]);

  const generateSchemePdf = (scheme, gifts) => {
    const doc = new jsPDF();

    // Title: Party Name and Schemes for Product Name
    doc.setFontSize(16);
    doc.text(`Party Name: ${selectedCustomer.Party_Name}`, 10, 10);
    doc.setFontSize(14);
    doc.text(`Schemes for ${scheme.productInfo.brand_name}`, 10, 20);

    // Product Info Section
    let productInfoStartY = 30;
    doc.setFontSize(12);
    doc.text(`Product Information`, 10, productInfoStartY);

    const productDetails = [
      `Composition: ${scheme.productInfo.compostion}`,
      `Packing: ${scheme.productInfo.packing}`,
      `MRP: ${scheme.productInfo.mrp}/- per ${scheme.productInfo.unit}`,
      `Rate: ${scheme[3]}/- per ${scheme.productInfo.unit}`,
      `Company: ${scheme.productInfo.company}`,
    ];

    productDetails.forEach((detail, index) => {
      productInfoStartY += 7; // Adjust the line spacing
      doc.text(detail, 10, productInfoStartY);
    });

    // Add a line separator for visual clarity
    productInfoStartY += 7;
    doc.line(10, productInfoStartY, 200, productInfoStartY);

    // Schemes Section: Start after leaving some space from the product info
    let schemesStartY = productInfoStartY + 10; // Adjust startY for Schemes section based on the last Y position of product info

    // Base category value for calculation
    const baseCategoryValue = 5000;
    const categories = [
      ...new Set(gifts.map((gift) => parseInt(gift.category))),
    ].sort((a, b) => a - b);

    categories.forEach((category, index) => {
      const requiredQty = Math.round(
        scheme[1] * (category / baseCategoryValue)
      );
      const giftsForCategory = gifts
        .filter((gift) => parseInt(gift.category) === category)
        .map((gift) => [
          gift.gift_name,
          `Buy ${requiredQty} ${scheme.productInfo.unit}`,
        ]);

      doc.autoTable({
        head: [[`Gifts for Category ${category}`, "Requirement"]],
        body: giftsForCategory,
        startY: schemesStartY,
        theme: "striped",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [22, 160, 133] }, // Adjust colors
        margin: { top: 10 },
      });

      // Adjust schemesStartY for the next category based on the previous table's final Y position
      schemesStartY = doc.lastAutoTable.finalY + 10;
    });

    // Save the PDF
    doc.save(`${scheme.productInfo.brand_name}-schemes-and-gifts.pdf`);
  };

  useEffect(() => {
    if (selectedCustomer) {
      setSchemeType("old");
      // Fetch Old Regular Items Schemes
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/api/customer-schemes-id/${selectedCustomer.customer_id}`
        )
        .then((response) => {
          const filteredOldSchemes = response.data.filter(
            (scheme) => scheme.productInfo
          );
          setSchemes(filteredOldSchemes);
        })
        .catch((error) => {
          console.error("Error fetching customer schemes:", error);
          setSchemes([]);
        });

      // Fetch New Items Schemes
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/api/new-customer-schemes-id/${selectedCustomer.customer_id}`
        )
        .then((response) => {
          const filteredNewSchemes = response.data.filter(
            (scheme) => scheme.productInfo
          );
          setNewSchemes(filteredNewSchemes);
        })
        .catch((error) => {
          console.error("Error fetching new customer schemes:", error);
          setNewSchemes([]);
        });

      // Fetch gifts data from the backend
      axios
        .get(`${process.env.REACT_APP_API_URL}/api/gifts`)
        .then((response) => {
          setAllGifts(response.data);
        })
        .catch((error) => {
          console.error("Error fetching gifts:", error);
        });
    }
  }, [selectedCustomer]);

  const handleSchemeClick = (scheme) => {
    setSelectedScheme(scheme);
    setIsGiftModalOpen(true);
  };
  const getImagePath = (company, imageName) => {
    // Base URL for images in production - adjust the domain as necessary
    let basePath = "";

    // console.log(company);

    // Determine the base path based on the company
    switch (company) {
      case "Trumac":
        basePath = "/app_admin/assets/images/products";
        break;
      case "Camrut":
        basePath = "/camrut_app_admin/assets/images/products";
        break;
      case "Medisync":
        basePath = "/medisync_app_admin/assets/images/products";
        break;
      case "Cialto Drugs":
        basePath = "/cialto_app_admin/assets/images/products";
        break;
      case "Pythus Pharma":
        basePath = "/pythus_app_admin/assets/images/products";
        break;
      default:
        basePath = "/images"; // Default path for development or fallback
    }

    // console.log(basePath);

    // Check if the environment is development or production
    // This is a basic check, you might need to adjust it based on your setup
    const isDevelopment =
      !process.env.NODE_ENV || process.env.NODE_ENV === "development";

    // console.log("mode : ", process.env.NODE_ENV);

    // Return the correct path
    return isDevelopment
      ? `https://trumachealthcare.net${basePath}/${imageName}`
      : `${basePath}/${imageName}`;
  };
  // Function to calculate the yearly incentive
  const calculateYearlyIncentive = (yearlyTarget) => {
    return Math.round(yearlyTarget * 100000 * 0.03); // 3% of the yearly target
  };

  // Function to calculate the monthly incentive
  const calculateMonthlyIncentive = (monthlyTarget) => {
    return Math.round(monthlyTarget * 100000 * 0.02); // 2% of the monthly target
  };

  // Function to format the last_updated date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/employee/customers?mobile=${mobile}`
        );
        setCustomers(response.data);
        // Assuming the employee name is consistent across customer records
        if (response.data.length > 0) {
          setEmployeeName(response.data[0].Employee);
        }

        if (mobile === "9041677932") {
          setEmployeeName("Admin");
        }
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    };

    if (mobile) {
      fetchCustomers();
    }
  }, [mobile]);

  const downloadAllSchemes = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Determine which schemes to download
    const currentSchemes = schemeType === "old" ? filteredSchemes : newSchemes;

    // Center align the top heading
    const title = `SCHEMES FOR ${selectedCustomer.Party_Name}`;
    const titleX = (pageWidth - doc.getTextWidth(title)) / 2;
    doc.setFontSize(16);
    doc.text(title, titleX, 10);

    // Table headers
    const tableColumns = [
      { content: "S.No", styles: { halign: "center" } },
      { content: "Brand Name", styles: { halign: "center" } },
      { content: "Composition", styles: { halign: "center" } },
      { content: "Qty (Unit)", styles: { halign: "center" } },
      { content: "Category", styles: { halign: "center" } },
    ];
    const tableData = [];

    // Prepare data for each scheme
    currentSchemes.forEach((scheme, index) => {
      const serialNo = index + 1;
      const brandName = scheme.productInfo.brand_name;
      const composition = scheme.productInfo.compostion;
      const qty = `${Math.round(scheme[1] * (5000 / 5000))} ${
        scheme.productInfo.unit
      }`;
      const category = 5000;

      tableData.push([serialNo, brandName, composition, qty, category]);
    });

    doc.autoTable({
      head: [tableColumns.map((col) => col.content)], // Use just the text for headers
      body: tableData,
      startY: 20,
      theme: "striped",
      columnStyles: {
        0: { cellWidth: 15, halign: "center" }, // Serial Number, center aligned
        1: { cellWidth: 40 }, // Brand Name
        2: { fontSize: 10 }, // Composition, decreased font size
        3: { cellWidth: 30, halign: "center" }, // Qty (Unit), increased cell width
        4: { halign: "center" }, // Center align contents of Category column
      },
      styles: {
        fontSize: 10,
        cellPadding: 3,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      }, // General font size, add borders
      headStyles: { fillColor: [22, 160, 133], halign: "center" }, // Center-align headers and adjust colors
      tableLineColor: [0, 0, 0], // Border color for table
      tableLineWidth: 0.5, // Border width
    });

    // Prepare data for gifts table
    let giftsStartY = doc.lastAutoTable.finalY + 20;
    const giftCategories = [
      ...new Set(allGifts.map((gift) => gift.category)),
    ].sort((a, b) => a - b);

    const giftsTableData = [];
    giftCategories.forEach((category) => {
      const giftsForCategory = allGifts
        .filter((gift) => gift.category === category)
        .map((gift, index) => [index + 1, gift.gift_name, category]);

      giftsTableData.push(...giftsForCategory);
    });

    // Generate gifts table
    doc.autoTable({
      head: [["S.No", "Gift Name", "Category"]],
      body: giftsTableData,
      startY: giftsStartY,
      theme: "striped",
      columnStyles: {
        0: { cellWidth: 15, halign: "center" }, // Serial Number, center aligned
        1: { cellWidth: "auto" }, // Gift Name, auto width to take maximum space
        2: { halign: "center", cellWidth: 25 }, // Center align contents of Category column
      },
      styles: {
        fontSize: 10,
        cellPadding: 3,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      }, // General font size, add borders
      headStyles: { fillColor: [22, 160, 133], halign: "center" }, // Center-align headers and adjust colors
      tableLineColor: [0, 0, 0], // Border color for table
      tableLineWidth: 0.5, // Border width
    });

    // Save the PDF
    doc.save(`Schemes-for-${selectedCustomer.Party_Name}.pdf`);
  };

  const filteredCustomers = searchText.trim()
    ? customers
        .filter(
          (customer) =>
            customer.Party_Name.toLowerCase().includes(
              searchText.toLowerCase()
            ) ||
            customer.Company.toLowerCase().includes(searchText.toLowerCase()) ||
            customer.City.toLowerCase().includes(searchText.toLowerCase())
        )
        // Reduce the array to only include unique entries by Party_Name
        .reduce((unique, customer) => {
          const isNotListed = !unique.some(
            (uniqueCustomer) =>
              uniqueCustomer.Party_Name === customer.Party_Name
          );
          if (isNotListed) {
            unique.push(customer);
          }
          return unique;
        }, [])
    : [];

  const calculatePercentage = (current, target) => {
    return Math.min(100, (current / target) * 100).toFixed(2);
  };

  return (
    <div className="employee-dashboard-container">
      <div className="employee-dashboard-header">
        <h1>Welcome, {employeeName}</h1>
      </div>
      <div className="customer-search-container">
        <input
          type="text"
          placeholder="Search customers..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="search-input"
        />
        {searchText && (
          <button onClick={() => setSearchText("")} className="clear-search">
            &#10005;
          </button>
        )}
        {filteredCustomers.length > 0 && (
          <ul className="search-results-dropdown">
            {filteredCustomers.map((customer) => (
              <li
                key={customer.customer_sale_data_id}
                onClick={() => {
                  setSelectedCustomer(customer);
                  setSearchText("");
                }}
              >
                {customer.Party_Name}
              </li>
            ))}
          </ul>
        )}
      </div>
      {selectedCustomer ? (
        <>
          <div className="customer-details">
            <h1>{selectedCustomer.Party_Name}</h1>

            {/* Additional customer details here */}
            <div className="detail-blocks-container">
              {selectedCustomer && (
                <div className="customer-details-container">
                  <div className="detail-block">
                    <p>
                      <strong>Employee:</strong> {selectedCustomer.Employee}
                    </p>
                    <p>
                      <strong>City:</strong> {selectedCustomer.City}
                    </p>
                    <p>
                      <strong>State:</strong> {selectedCustomer.State}
                    </p>
                    <p>
                      <strong>Working with us since : </strong>{" "}
                      {selectedCustomer.age} Months
                    </p>
                    <p>
                      <strong>Last Year 2023-24 Sales (With GST):</strong>{" "}
                      {selectedCustomer.last_year_sale}/-
                    </p>
                    <p>
                      <strong>Last Month Sales (With GST):</strong>{" "}
                      {selectedCustomer.last_month_sale}/-
                    </p>
                    {/* <p>
                      <strong>Avg Sale Per Month : </strong>{" "}
                      {Math.round(selectedCustomer.Avg_Sale)}/-
                    </p> */}
                  </div>
                  <div className="detail-block">
                    <h2>
                      Yearly Target & Sales<div>(2024-2025)</div>
                    </h2>
                    <div className="progress-bar-container">
                      <CircularProgressbar
                        value={calculatePercentage(
                          selectedCustomer.Total_Syn_Sales_Yearly,
                          selectedCustomer.Yearly_Target * 100000
                        )}
                        text={`${calculatePercentage(
                          selectedCustomer.Total_Syn_Sales_Yearly,
                          selectedCustomer.Yearly_Target * 100000
                        )}%`}
                        styles={buildStyles({
                          pathColor: `rgba(62, 152, 199, ${
                            calculatePercentage(
                              selectedCustomer.Total_Syn_Sales_Yearly,
                              selectedCustomer.Yearly_Target * 100000
                            ) / 100
                          })`,
                          textColor: "#f88",
                          trailColor: "#d6d6d6",
                          backgroundColor: "#3e98c7",
                        })}
                      />
                    </div>
                    <p>
                      <strong>Yearly Target (Without GST) :</strong> ₹
                      {Math.round(selectedCustomer.Yearly_Target * 100000)}/-
                    </p>
                    <p>
                      <strong>Yearly Incentive:</strong> ₹
                      {calculateYearlyIncentive(selectedCustomer.Yearly_Target)}
                      /-
                    </p>
                    <p>
                      <strong>Current Year Sales (With GST):</strong> ₹
                      {Math.round(
                        selectedCustomer.Total_Syn_Sales_Yearly * 1.14
                      )}
                      /-
                    </p>
                    <p>
                      <strong>Current Year Sales (Without GST):</strong> ₹
                      {Math.round(selectedCustomer.Total_Syn_Sales_Yearly)}/-
                    </p>
                    {/* <p className="highlighted-row">
                      <strong>Yearly Target 2024-25 (Without GST):</strong> ₹
                      {Math.round(selectedCustomer.yearly_target_2425 * 100000)}
                      /-
                    </p> */}
                    <p>
                      <strong>Last Updated:</strong>{" "}
                      {formatDate(selectedCustomer.last_updated)}
                    </p>
                  </div>
                  <div className="detail-block">
                    <h2>
                      Monthly Target & Sales
                      <div>({process.env.REACT_APP_CURRENT_MONTH} 2024)</div>
                    </h2>
                    <div className="progress-bar-container">
                      <CircularProgressbar
                        value={calculatePercentage(
                          selectedCustomer.Total_Syn_Sales_Monthly,
                          selectedCustomer.Monthly_Target * 100000
                        )}
                        text={`${calculatePercentage(
                          selectedCustomer.Total_Syn_Sales_Monthly,
                          selectedCustomer.Monthly_Target * 100000
                        )}%`}
                        styles={buildStyles({
                          pathColor: `rgba(255, 68, 0, ${
                            calculatePercentage(
                              selectedCustomer.Total_Syn_Sales_Monthly,
                              selectedCustomer.Monthly_Target * 100000
                            ) / 100
                          })`,
                          textColor: "#f88",
                          trailColor: "#d6d6d6",
                          backgroundColor: "#3e98c7",
                        })}
                      />
                    </div>
                    <p>
                      <strong>Monthly Target (Without GST) :</strong> ₹
                      {Math.round(selectedCustomer.Monthly_Target * 100000)}/-
                    </p>
                    <p>
                      <strong>Monthly Incentive:</strong> ₹
                      {calculateMonthlyIncentive(
                        selectedCustomer.Monthly_Target
                      )}
                      /-
                    </p>
                    <p>
                      <strong>Current Month Sales (With GST):</strong> ₹
                      {Math.round(
                        selectedCustomer.Total_Syn_Sales_Monthly * 1.14
                      )}
                      /-
                    </p>
                    <p>
                      <strong>Current Month Sales (Without GST):</strong> ₹
                      {Math.round(selectedCustomer.Total_Syn_Sales_Monthly)}/-
                    </p>
                    {selectedCustomer.Total_Syn_Sales_Monthly >=
                      selectedCustomer.Monthly_Target * 100000 * 0.99 && (
                      <p>
                        <strong>Current Month Incentive:</strong> ₹
                        {Math.round(
                          selectedCustomer.Total_Syn_Sales_Monthly * 0.02
                        )}
                        /-
                      </p>
                    )}

                    <p className="highlighted-row">
                      <strong>Freight Free Target (Without GST):</strong> ₹
                      {Math.round(
                        selectedCustomer.Monthly_Target * 1.2 * 100000
                      )}
                      /-
                    </p>
                    <p>
                      <strong>Last Updated:</strong>{" "}
                      {formatDate(selectedCustomer.last_updated)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="customer-schemes-container">
            <div className="schemes-header">
              <h1 className="schemes-title">
                SCHEMES FOR {selectedCustomer.Party_Name}
              </h1>
              <button
                onClick={downloadAllSchemes}
                className="download-all-schemes-btn"
              >
                Download All Schemes
              </button>
            </div>
            <div className="scheme-toggle-buttons">
              <button
                onClick={() => setSchemeType("old")}
                className={schemeType === "old" ? "active" : ""}
              >
                Old / Regular Products Schemes
              </button>
              <button
                onClick={() => setSchemeType("new")}
                className={schemeType === "new" ? "active" : ""}
              >
                New Products Schemes
              </button>
            </div>
            {/* <h1>Schemes for {selectedCustomer.Party_Name}</h1> */}
            <p>Click on the product to view schemes</p>
            <div className="sticky-search-bar">
              <input
                type="text"
                placeholder="Search brand name, composition, segment..."
                value={schemeSearchText}
                onChange={(e) => setSchemeSearchText(e.target.value)}
                className="search-input"
              />
            </div>
            {schemeType === "old" ? (
              <div className="schemes-grid">
                {filteredSchemes.map((scheme, index) => (
                  <div
                    key={index}
                    className="scheme-card"
                    onClick={() => handleSchemeClick(scheme)}
                  >
                    {scheme.productInfo ? (
                      <>
                        <img
                          src={getImagePath(
                            scheme.productInfo.company,
                            scheme.productInfo.product_image
                          )}
                          // src={scheme.productInfo.product_image} // Adjust as necessary for your image path
                          alt={scheme.productInfo.brand_name}
                          className="scheme-image"
                        />
                        <div className="scheme-info">
                          <h3>{scheme.productInfo.brand_name}</h3>
                          <p>
                            <strong>Composition:</strong>{" "}
                            {scheme.productInfo.compostion}
                          </p>
                          <p>
                            <strong>Packing:</strong>{" "}
                            {scheme.productInfo.packing}
                          </p>
                          <p>
                            <strong>MRP:</strong> ₹{scheme.productInfo.mrp}
                          </p>
                          <p>
                            <strong>Rate:</strong> ₹{scheme[3]}/-
                          </p>
                          <p>
                            <strong>Company:</strong>{" "}
                            {scheme.productInfo.company}
                          </p>
                          <span className="view-schemes-link">
                            Click to View Schemes
                          </span>{" "}
                          {/* Link to view schemes */}
                        </div>
                      </>
                    ) : (
                      <p>Product details not available.</p>
                    )}
                    <button
                      onClick={(event) => {
                        event.stopPropagation(); // Prevent opening the GiftModal
                        generateSchemePdf(scheme, allGifts);
                      }}
                      className="download-scheme-btn"
                    >
                      Download Scheme
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="schemes-grid">
                {filteredSchemes.map((scheme, index) => (
                  <div
                    key={index}
                    className="scheme-card"
                    onClick={() => handleSchemeClick(scheme)}
                  >
                    {scheme.productInfo ? (
                      <>
                        <img
                          src={getImagePath(
                            scheme.productInfo.company,
                            scheme.productInfo.product_image
                          )}
                          // src={scheme.productInfo.product_image} // Adjust as necessary for your image path
                          alt={scheme.productInfo.brand_name}
                          className="scheme-image"
                        />
                        <div className="scheme-info">
                          <h3>{scheme.productInfo.brand_name}</h3>
                          <p>
                            <strong>Composition:</strong>{" "}
                            {scheme.productInfo.compostion}
                          </p>
                          <p>
                            <strong>Packing:</strong>{" "}
                            {scheme.productInfo.packing}
                          </p>
                          <p>
                            <strong>MRP:</strong> ₹{scheme.productInfo.mrp}
                          </p>
                          <p>
                            <strong>Rate:</strong> ₹{scheme[3]}/-
                          </p>
                          <p>
                            <strong>Company:</strong>{" "}
                            {scheme.productInfo.company}
                          </p>
                          <span className="view-schemes-link">
                            Click to View Schemes
                          </span>{" "}
                          {/* Link to view schemes */}
                        </div>
                      </>
                    ) : (
                      <p>Product details not available.</p>
                    )}
                    <button
                      onClick={(event) => {
                        event.stopPropagation(); // Prevent opening the GiftModal
                        generateSchemePdf(scheme, allGifts);
                      }}
                      className="download-scheme-btn"
                    >
                      Download Scheme
                    </button>
                  </div>
                ))}
              </div>
            )}
            <GiftModal
              isOpen={isGiftModalOpen}
              onClose={() => setIsGiftModalOpen(false)}
              scheme={selectedScheme}
              gifts={allGifts}
            />
          </div>
        </>
      ) : (
        <>
          <p>Select a Customer by searching above...</p>
        </>
      )}
    </div>
  );
};

export default EmployeeDashboard;
